// The alignments (docs/02-Game-Design/Alignments.md): each planet's five
// candidate rules, by id. A level offers three of its planet's five
// (ALIGNMENTS_PER_PLANET); the player who first surrounds the planet picks
// one for the night. This file is the catalogue only (ids, planets, names):
// what each alignment does lives in its own file under alignments/<planet>/
// and is wired up in registry.ts, so the schema and the designer can name an
// alignment without loading its rules.
import { PLANET_KINDS, type PlanetKind } from '../level/presets';

/** How many of a planet's alignments a level offers. */
export const ALIGNMENTS_PER_PLANET = 3;

export const ALIGNMENT_IDS = [
  // Mercury
  'retrograde',
  'crossroads',
  'surety',
  'seeing-double',
  'perception',
  // Venus
  'mellifluence',
  'first-impression',
  'symmetry',
  'embrace',
  'climax',
  // Mars
  'collateral-damage',
  'volatility',
  'wrath',
  'javelin',
  'ultimatum',
  // Jupiter
  'enormity',
  'majesty',
  'elitism',
  'breadth',
  'great-red-spot',
  // Saturn
  'orderliness',
  'capability',
  'foundations',
  'discipline',
  'balance',
  // Uranus
  'diversity',
  'rarities',
  'cacophony',
  'explosiveness',
  'uniqueness',
  // Neptune
  'sacrifice',
  'depth',
  'solitude',
  'trident',
  'abyss',
  // Pluto
  'transformation',
  'beginnings-and-ends',
  'underdog',
  'far-reaches',
  'riches',
] as const;
export type AlignmentId = (typeof ALIGNMENT_IDS)[number];

export interface AlignmentEntry {
  id: AlignmentId;
  planet: PlanetKind;
  name: string;
}

const ENTRIES: ReadonlyArray<readonly [AlignmentId, PlanetKind, string]> = [
  ['retrograde', 'mercury', 'Retrograde'],
  ['crossroads', 'mercury', 'Crossroads'],
  ['surety', 'mercury', 'Surety'],
  ['seeing-double', 'mercury', 'Seeing Double'],
  ['perception', 'mercury', 'Perception'],
  ['mellifluence', 'venus', 'Mellifluence'],
  ['first-impression', 'venus', 'First Impression'],
  ['symmetry', 'venus', 'Symmetry'],
  ['embrace', 'venus', 'Embrace'],
  ['climax', 'venus', 'Climax'],
  ['collateral-damage', 'mars', 'Collateral Damage'],
  ['volatility', 'mars', 'Volatility'],
  ['wrath', 'mars', 'Wrath'],
  ['javelin', 'mars', 'Javelin'],
  ['ultimatum', 'mars', 'Ultimatum'],
  ['enormity', 'jupiter', 'Enormity'],
  ['majesty', 'jupiter', 'Majesty'],
  ['elitism', 'jupiter', 'Elitism'],
  ['breadth', 'jupiter', 'Breadth'],
  ['great-red-spot', 'jupiter', 'Great Red Spot'],
  ['orderliness', 'saturn', 'Orderliness'],
  ['capability', 'saturn', 'Capability'],
  ['foundations', 'saturn', 'Foundations'],
  ['discipline', 'saturn', 'Discipline'],
  ['balance', 'saturn', 'Balance'],
  ['diversity', 'uranus', 'Diversity'],
  ['rarities', 'uranus', 'Rarities'],
  ['cacophony', 'uranus', 'Cacophony'],
  ['explosiveness', 'uranus', 'Explosiveness'],
  ['uniqueness', 'uranus', 'Uniqueness'],
  ['sacrifice', 'neptune', 'Sacrifice'],
  ['depth', 'neptune', 'Depth'],
  ['solitude', 'neptune', 'Solitude'],
  ['trident', 'neptune', 'Trident'],
  ['abyss', 'neptune', 'Abyss'],
  ['transformation', 'pluto', 'Transformation'],
  ['beginnings-and-ends', 'pluto', 'Beginnings & Ends'],
  ['underdog', 'pluto', 'Underdog'],
  ['far-reaches', 'pluto', 'Far Reaches'],
  ['riches', 'pluto', 'Riches'],
];

/** Every alignment, in catalogue order (planet by planet, from the Sun). */
export const ALIGNMENT_CATALOGUE: ReadonlyArray<AlignmentEntry> = ENTRIES.map(([id, planet, name]) => ({ id, planet, name }));

const BY_ID: ReadonlyMap<AlignmentId, AlignmentEntry> = new Map(ALIGNMENT_CATALOGUE.map((e) => [e.id, e]));

export function isAlignmentId(id: string): id is AlignmentId {
  return BY_ID.has(id as AlignmentId);
}

export function alignmentEntry(id: AlignmentId): AlignmentEntry {
  return BY_ID.get(id)!;
}

export function alignmentName(id: AlignmentId): string {
  return BY_ID.get(id)!.name;
}

/** A planet's five alignments, in catalogue order. */
export function alignmentsOf(kind: PlanetKind): AlignmentId[] {
  return ALIGNMENT_CATALOGUE.filter((e) => e.planet === kind).map((e) => e.id);
}

/** The planet an alignment belongs to. */
export function planetOfAlignment(id: AlignmentId): PlanetKind {
  return BY_ID.get(id)!.planet;
}

/** The order the pipeline applies alignments in when it matters (the base-value sets): planet by planet, from the Sun. */
export function planetOrder(kind: PlanetKind): number {
  return PLANET_KINDS.indexOf(kind);
}

/** The planet's name as the player reads it. */
export function planetName(kind: PlanetKind): string {
  return kind[0]!.toUpperCase() + kind.slice(1);
}
